"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const CheckIcon = () => (
	<span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#64a525] text-xs text-white">
		✓
	</span>
);

export interface PlasticInjectionFourPhasesProps {
	/** 翻译命名空间，见 en.json `plasticInjectionMoldingFourPhases` */
	translationNamespace?: string;
	/** 顶部右侧大图（1.jpg） */
	heroImageSrc: string;
	/** 四宫格配图：顺序对应阶段 1～4（2.jpg～5.jpg） */
	phaseImageSrcs: readonly [string, string, string, string];
}

/**
 * 「4 Key Phases in Plastic Injection Molding Solutions」
 * 上：左文案（标题 + 简介 + 三条勾选 + 引导句）+ 右主图；下：2×2 阶段卡片（左图右文）
 */
export default function PlasticInjectionFourPhases({
	translationNamespace = "plasticInjectionMoldingFourPhases",
	heroImageSrc,
	phaseImageSrcs,
}: PlasticInjectionFourPhasesProps) {
	const t = useTranslations(translationNamespace);

	const bullets = [t("bullet1"), t("bullet2"), t("bullet3")] as const;

	const phases = [
		{ titleKey: "phase1Title", descKey: "phase1Desc", image: phaseImageSrcs[0], altKey: "phase1ImageAlt" },
		{ titleKey: "phase2Title", descKey: "phase2Desc", image: phaseImageSrcs[1], altKey: "phase2ImageAlt" },
		{ titleKey: "phase3Title", descKey: "phase3Desc", image: phaseImageSrcs[2], altKey: "phase3ImageAlt" },
		{ titleKey: "phase4Title", descKey: "phase4Desc", image: phaseImageSrcs[3], altKey: "phase4ImageAlt" },
	] as const;

	return (
		<section className="bg-white pb-8 sm:pb-10 md:pb-12 lg:pb-16">
			<div className="container">
				{/* ── 上：文案与主图各占一半，与相邻介绍区的图文比例保持一致 ── */}
				<div className="mb-6 flex flex-col gap-8 sm:mb-8 md:mb-10 lg:mb-12 lg:flex-row lg:items-stretch lg:gap-8 xl:gap-10">
					<div className="min-w-0 flex-1">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("sectionTitle")}
						</h2>
						<p className="mb-6 text-sm leading-relaxed text-black md:mb-8 md:text-base">
							{t("introParagraph")}
						</p>
						<ul className="mb-6 space-y-0 md:mb-8">
							{bullets.map((text, index) => (
								<li
									key={index}
									className="flex gap-3 border-b border-gray-200 py-4 first:pt-0 last:border-b-0 last:pb-0"
								>
									<CheckIcon />
									<span className="text-sm leading-relaxed text-black md:text-base">{text}</span>
								</li>
							))}
						</ul>
						<p className="text-sm font-medium text-black sm:text-base">{t("leadInSentence")}</p>
					</div>

					<div className="relative w-full flex-1 lg:min-w-0">
						<div className="relative mx-auto aspect-[4/3] w-full max-w-xl overflow-hidden shadow-lg lg:aspect-auto lg:h-full lg:min-h-[480px] lg:max-w-none">
							<Image
								src={heroImageSrc}
								alt={t("heroImageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>
				</div>

				{/* ── 下：四阶段 2×2 ── */}
				<div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
					{phases.map((phase) => (
						<div
							key={phase.titleKey}
							className="flex flex-col gap-4 overflow-hidden rounded-lg border border-gray-100 bg-[#fafafa] shadow-sm sm:flex-row sm:items-stretch sm:gap-0"
						>
							<div className="relative aspect-[4/3] w-full shrink-0 sm:aspect-auto sm:min-h-[220px] sm:w-[45%] md:min-h-[240px]">
								<Image
									src={phase.image}
									alt={t(phase.altKey)}
									fill
									className="object-cover"
									sizes="(max-width: 640px) 100vw, 45vw"
								/>
							</div>
							<div className="flex flex-1 flex-col justify-center px-4 py-4 sm:px-5 sm:py-5 md:px-6">
								<h3 className="mb-3 text-lg font-bold text-themePrimary400 sm:text-xl md:text-2xl">
									{t(phase.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(phase.descKey)}</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
